/**
 * Compliance Check Pando · Modal para ofertas_comercial.
 *
 * Llama a `POST /api/ofertas-comercial/{oid}/compliance-check`, muestra los
 * resultados (verificaciones, bloqueantes, alertas, recomendaciones).
 * Resultado se persiste en `doc.compliance_check`.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API } from "@/context/AuthContext";
import { postAiJob } from "@/utils/aiJobs";
import { X, ShieldCheck, AlertTriangle, AlertCircle, CheckCircle2, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const NIVEL_CFG = {
  ok:         { cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2, color: "#16A34A" },
  atencion:   { cls: "bg-amber-100 text-amber-700",     Icon: AlertTriangle, color: "#D97706" },
  bloqueante: { cls: "bg-rose-100 text-rose-700",       Icon: AlertCircle,  color: "#DC2626" },
};

const VERIF_KEYS = ["plazos","presupuesto","encaje_negocio","plataforma","competencia","datos_oferta"];

export default function ComplianceCheckModal({ oferta, onClose }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [result, setResult]   = useState(oferta?.compliance_check || null);

  const run = async () => {
    setLoading(true);
    try {
      const res = await postAiJob(`${API}/ofertas-comercial/${oferta.id}/compliance-check`);
      setResult(res);
      toast.success(`${t("compliance.title")} · ${(res.cumplimiento || "").toUpperCase()}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (!result) run(); /* eslint-disable-next-line */ }, []);

  const nivelCfg = NIVEL_CFG[result?.cumplimiento] || NIVEL_CFG.atencion;
  const NivelIcon = nivelCfg.Icon;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="compliance-modal">
      <div className="bg-white rounded-xl max-w-3xl w-full my-8 shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between sticky top-0 bg-white rounded-t-xl">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <ShieldCheck size={20} className="text-emerald-700"/>
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-gray-900">{t("compliance.title")}</h3>
              <p className="text-xs text-gray-500 mt-0.5 truncate">{oferta?.cliente} · {oferta?.objeto_contrato}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={run} disabled={loading} className="text-xs flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-2.5 py-1.5 rounded disabled:opacity-50" data-testid="compliance-rerun">
              {loading ? <Loader2 size={11} className="animate-spin"/> : <RefreshCw size={11}/>}
              {result ? t("compliance.rerun") : t("compliance.generating")}
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18}/></button>
          </div>
        </div>

        {/* Loading */}
        {loading && !result && (
          <div className="p-10 text-center text-gray-500 text-sm">
            <Loader2 size={28} className="animate-spin mx-auto text-emerald-600 mb-3"/>
            {t("compliance.analyzing")}
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="p-5 space-y-5">
            {/* Iter 124 fix: si el JSON de la Pando es truthy pero no trae claves esperadas
                mostramos aviso y volcado JSON para que el usuario pueda actuar en vez
                de ver el modal en blanco. */}
            {!result.resumen_ejecutivo && !result.verificaciones && !result.bloqueantes?.length && !result.recomendaciones?.length ? (
              <div className="rounded-lg border-l-4 border-amber-500 bg-amber-50 p-4" data-testid="compliance-empty-fallback">
                <p className="font-bold text-amber-900 mb-1">⚠️ La Pando no devolvió un análisis completo</p>
                <p className="text-xs text-amber-800 mb-3">El pliego pudo estar demasiado escueto o la respuesta se truncó. Puedes reintentar el check o revisar la oferta manualmente.</p>
                <button onClick={run} className="text-xs px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-700 text-white font-semibold mr-2" data-testid="compliance-retry-btn">Reintentar análisis</button>
                <details className="mt-2">
                  <summary className="text-xs text-amber-700 cursor-pointer">Ver JSON crudo</summary>
                  <pre className="mt-1 text-[10px] bg-white border border-amber-200 rounded p-2 overflow-auto max-h-40">{JSON.stringify(result, null, 2)}</pre>
                </details>
              </div>
            ) : (
              <>
            {/* Veredicto */}
            <div className={`rounded-lg p-4 border-l-4 ${nivelCfg.cls.replace('100','50')}`} style={{ borderLeftColor: nivelCfg.color }}>
              <div className="flex items-center gap-2 mb-2">
                <NivelIcon size={18} style={{ color: nivelCfg.color }}/>
                <span className={`text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded ${nivelCfg.cls}`}>
                  {t(`compliance.nivel.${result.cumplimiento || "atencion"}`)}
                </span>
                {typeof result.score_compliance === "number" && (
                  <span className="ml-auto text-xs text-gray-600">
                    Score: <strong style={{color: nivelCfg.color}}>{result.score_compliance}/100</strong>
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-800 leading-relaxed">{result.resumen_ejecutivo}</p>
            </div>

            {/* Verificaciones */}
            {result.verificaciones && (
              <section>
                <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">{t("compliance.verifications")}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {VERIF_KEYS.map(key => {
                    const v = result.verificaciones[key];
                    if (!v) return null;
                    const cfg = NIVEL_CFG[v?.estado] || NIVEL_CFG.atencion;
                    const Icn = cfg.Icon;
                    return (
                      <div key={key} className="border border-gray-200 rounded-lg p-3 bg-white">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Icn size={13} style={{ color: cfg.color }}/>
                          <span className="text-xs font-semibold text-gray-800">{t(`compliance.verif.${key}`)}</span>
                          <span className={`ml-auto text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${cfg.cls}`}>
                            {v?.estado || "?"}
                          </span>
                        </div>
                        <p className="text-[11px] text-gray-700 leading-snug">{v?.comentario}</p>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Bloqueantes */}
            {result.bloqueantes?.length > 0 && (
              <section>
                <h4 className="text-xs font-bold uppercase tracking-wide text-rose-700 mb-2 flex items-center gap-1">
                  <AlertCircle size={13}/> {t("compliance.blockers")} ({result.bloqueantes.length})
                </h4>
                <ul className="space-y-1.5">
                  {result.bloqueantes.map((b, i) => (
                    <li key={i} className="text-xs text-gray-800 bg-rose-50 border-l-2 border-rose-400 px-3 py-2 rounded">{b}</li>
                  ))}
                </ul>
              </section>
            )}

            {/* Alertas */}
            {result.alertas?.length > 0 && (
              <section>
                <h4 className="text-xs font-bold uppercase tracking-wide text-amber-700 mb-2 flex items-center gap-1">
                  <AlertTriangle size={13}/> {t("compliance.alerts")} ({result.alertas.length})
                </h4>
                <ul className="space-y-1.5">
                  {result.alertas.map((a, i) => (
                    <li key={i} className="text-xs text-gray-800 bg-amber-50 border-l-2 border-amber-400 px-3 py-2 rounded">{a}</li>
                  ))}
                </ul>
              </section>
            )}

            {/* Recomendaciones */}
            {result.recomendaciones?.length > 0 && (
              <section>
                <h4 className="text-xs font-bold uppercase tracking-wide text-emerald-700 mb-2 flex items-center gap-1">
                  <CheckCircle2 size={13}/> {t("compliance.recommendations")}
                </h4>
                <ul className="space-y-1.5">
                  {result.recomendaciones.map((r, i) => (
                    <li key={i} className="text-xs text-gray-800 bg-emerald-50 border-l-2 border-emerald-400 px-3 py-2 rounded">{r}</li>
                  ))}
                </ul>
              </section>
            )}

            {/* Conclusión */}
            {result.conclusion && (
              <section className="border-t border-gray-200 pt-4">
                <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">{t("compliance.conclusion")}</h4>
                <p className="text-sm text-gray-900 font-medium leading-relaxed">{result.conclusion}</p>
              </section>
            )}

            {/* Metadata */}
            {result.generated_at && (
              <p className="text-[10px] text-gray-400 text-right">
                {t("compliance.generatedBy", { date: new Date(result.generated_at).toLocaleString(), author: result.generated_by || "Sistema" })}
              </p>
            )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
